import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import Toggle from "../../components/common/Toggle";
import { useToast } from "../../contexts/ToastContext";
import { sitePagesService } from "../../services/sitePages";
import { sitePath } from "../../utils/urls";

const Templates = () => {
  const project = useSelector((state) => state.project.currentProject);
  const { info } = useToast();
  const [pages, setPages] = useState([]);
  const [savingHash, setSavingHash] = useState(null);
  const [sitemap, setSitemap] = useState(null);
  const [sitemapLoading, setSitemapLoading] = useState(false);
  const [sitemapOpen, setSitemapOpen] = useState(false);
  const discoverable = Boolean(project?.discoverable);

  useEffect(() => {
    if (!project) return;
    sitePagesService.list(project.hash, (response) =>
      setPages(Array.isArray(response.data) ? response.data : response.data.results),
    );
    setSitemapLoading(true);
    sitePagesService.sitemap(
      project.hash,
      (response) => {
        setSitemap(response.data);
        setSitemapLoading(false);
      },
      () => {
        setSitemap({ exists: false, count: 0, urls: [], url: "" });
        setSitemapLoading(false);
      },
    );
  }, [project]);

  const patchPage = (page, data, { sitemapToggled } = {}) => {
    if (!discoverable) return;
    setSavingHash(page.hash);
    sitePagesService.update(
      page.hash,
      project.hash,
      data,
      (response) => {
        const updated = response.data;
        setPages((items) => items.map((item) => (item.hash === updated.hash ? updated : item)));
        setSavingHash(null);
        if (sitemapToggled) {
          info(
            <>
              Redeploy the site to update the sitemap.{" "}
              <Link to={sitePath(project, "deploys/")} className="underline font-semibold">
                Open deploys
              </Link>
            </>,
            8000,
            { key: "sitemap-redeploy" },
          );
        }
      },
      () => setSavingHash(null),
    );
  };

  const toggleIndex = (page) => {
    const index = !page.index;
    patchPage(page, { index, ...(index ? {} : { sitemap: false }) });
  };

  const toggleSitemap = (page) => {
    if (!page.index) return;
    patchPage(page, { sitemap: !page.sitemap }, { sitemapToggled: true });
  };

  return (
    <>
      <div className="space-y-8">
        <section className={!discoverable ? "pointer-events-none opacity-50" : undefined}>
          <h2 className="mb-1 text-sm font-semibold">Templates</h2>
          <p className="mb-3 text-sm text-theme-text opacity-60">
            Index and sitemap settings for each page template in your live site.
          </p>
          <div className="overflow-hidden rounded-xl border border-theme-secondary">
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 bg-theme-secondary p-3 text-xs font-semibold">
              <span>Path</span>
              <span className="w-16 text-center">Index</span>
              <span className="w-20 text-center">Sitemap</span>
            </div>
            {pages.length ? (
              pages.map((page) => (
                <div
                  key={page.hash}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-4 border-t border-theme-secondary p-3 text-sm"
                >
                  <code className="truncate font-mono text-xs">{page.path}</code>
                  <div className="flex w-16 justify-center">
                    <Toggle
                      checked={page.index}
                      disabled={!discoverable || savingHash === page.hash}
                      onChange={() => toggleIndex(page)}
                      label="Index"
                    />
                  </div>
                  <div className="flex w-20 justify-center">
                    <Toggle
                      checked={page.sitemap}
                      disabled={!discoverable || !page.index || savingHash === page.hash}
                      onChange={() => toggleSitemap(page)}
                      label="Sitemap"
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="border-t border-theme-secondary p-5 text-sm opacity-60">
                Site pages appear after you publish to live.
              </p>
            )}
          </div>
        </section>

        <section className={!discoverable ? "pointer-events-none opacity-50" : undefined}>
          <h2 className="mb-1 text-sm font-semibold">Sitemap</h2>
          <p className="mb-3 text-sm text-theme-text opacity-60">
            Generated on production deploy from pages with Index and Sitemap enabled.
          </p>
          <div className="rounded-xl border border-theme-secondary p-4">
            {sitemapLoading ? (
              <p className="text-sm opacity-60">Checking live sitemap…</p>
            ) : sitemap?.exists ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-theme-text">
                    {sitemap.count.toLocaleString()} URL{sitemap.count === 1 ? "" : "s"} in the
                    live sitemap
                  </p>
                  {sitemap.url ? (
                    <a
                      href={sitemap.url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-xs text-theme-primary underline"
                    >
                      {sitemap.url}
                    </a>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setSitemapOpen(true)}
                  disabled={!discoverable}
                  className="rounded-lg border border-theme-secondary px-3 py-1.5 text-sm hover:opacity-80 disabled:cursor-not-allowed"
                >
                  Expand
                </button>
              </div>
            ) : (
              <p className="text-sm opacity-60">
                No sitemap yet.{" "}
                <Link to={sitePath(project, "deploys/")} className="text-theme-primary underline">
                  Publish a production deployment
                </Link>{" "}
                and it will show up here.
              </p>
            )}
          </div>
        </section>
      </div>

      {sitemapOpen && sitemap?.exists ? (
        <SitemapModal urls={sitemap.urls || []} onClose={() => setSitemapOpen(false)} />
      ) : null}
    </>
  );
};

const SitemapModal = ({ urls, onClose }) => {
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? urls.filter((url) => url.toLowerCase().includes(needle))
    : urls;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg border border-theme-secondary bg-theme-main">
        <div className="border-b border-theme-secondary px-5 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-theme-text">
              Sitemap ({filtered.length.toLocaleString()}
              {needle ? ` of ${urls.length.toLocaleString()}` : ""} URLs)
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="text-xl leading-none text-theme-text opacity-60 hover:opacity-100"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search URLs…"
            className="w-full rounded-lg border border-theme-secondary bg-theme-main px-3 py-2 text-sm text-theme-text outline-none focus:border-theme-primary"
            autoFocus
          />
        </div>
        <div className="overflow-auto p-5 font-mono text-xs leading-6 text-theme-text">
          {filtered.length ? (
            filtered.map((url) => (
              <div key={url} className="truncate">
                <a href={url} target="_blank" rel="noreferrer" className="hover:underline">
                  {url}
                </a>
              </div>
            ))
          ) : (
            <p className="opacity-60">
              {needle ? "No URLs match that search." : "No URLs found in the sitemap."}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Templates;
